import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { CalendarIntervalDay, SimpleCallback } from 'platform-components';
import React, { useMemo } from 'react';

import { Calendar } from 'common.components/calendar/calendar';

import { CourseCalendarCardTypeClesson } from '../__card/_type/course-calendar__card_type_clesson';
import { CourseCalendarLoadMore } from '../__load-more/course-calendar__load-more';
import { COURSE_CALENDAR_MIN_DATE } from '../constants';
import { CalendarCardType, CourseCalendarColumns } from '../typings';

interface CourseCalendarScheduleProps {
    editorId: string;
    teacherId?: number;
    classId?: string;
    interval: CalendarIntervalDay[];
    columns: Record<string, CourseCalendarColumns>;
    intervalLoadingStatus: LOADING_STATUSES;
    onPrevButtonClick: SimpleCallback;
    onNextButtonClick: SimpleCallback;
    forceCompleteLessonSuccess?: (day: CalendarIntervalDay) => void;
    onShowMoreClick: (day: CalendarIntervalDay, maxVisibleCards: number) => void;
}

export const CourseCalendarSchedule = ({
    editorId,
    teacherId,
    interval,
    columns,
    intervalLoadingStatus,
    onPrevButtonClick,
    onNextButtonClick,
    onShowMoreClick,
    forceCompleteLessonSuccess,
}: CourseCalendarScheduleProps) => {
    const calendarContent = useMemo(() => {
        // eslint-disable-next-line complexity
        return interval.reduce((content: Record<string, React.ReactNode[]>, day) => {
            const { date } = day;
            const cards = columns[date]?.cards || [];
            const maxVisibleCards = columns[date]?.maxVisibleCards;
            const isAllCardsLoaded = columns[date]?.isAllCardsLoaded;
            const isColumnLoading = columns[date]?.columnLoadingStatus === LOADING_STATUSES.LOADING;
            const allCardsCount = cards.length;

            content[date] = [];

            if (!allCardsCount || !maxVisibleCards) {
                return content;
            }

            for (let index = 0; index < maxVisibleCards && index < allCardsCount; index = index + 1) {
                const { cardId, cardType } = cards[index];

                switch (cardType) {
                    case CalendarCardType.CLESSON:
                        content[date].push(
                            <CourseCalendarCardTypeClesson
                                cardId={cardId}
                                cardType={cardType}
                                day={day}
                                editorId={editorId}
                                forceCompleteLessonSuccess={forceCompleteLessonSuccess}
                                key={cardId}
                                teacherId={teacherId}
                            />
                        );
                        break;
                    default:
                        throw new Error(`Не существующий тип карточки ${cardType} ${cardId}`);
                }
            }

            if (maxVisibleCards < allCardsCount || !isAllCardsLoaded) {
                content[date].push(
                    <CourseCalendarLoadMore
                        allCards={allCardsCount}
                        day={day}
                        hiddenCards={allCardsCount - maxVisibleCards}
                        isLoading={isColumnLoading}
                        onClick={onShowMoreClick}
                    />
                );
            }

            return content;
        }, {});
    }, [interval, columns, editorId, forceCompleteLessonSuccess, teacherId, onShowMoreClick]);

    return (
        <Calendar
            content={calendarContent}
            interval={interval}
            minDate={COURSE_CALENDAR_MIN_DATE}
            onNextButtonClick={onNextButtonClick}
            onPrevButtonClick={onPrevButtonClick}
            progress={intervalLoadingStatus === LOADING_STATUSES.LOADING}
        />
    );
};
